import { makeAutoObservable, toJS } from 'mobx'
import { ActionType } from 'xstate'
import { Action } from './Action'
import { ActionSpec, PersistedPropertyValue, StateConfig, StatePropertyValue, UnpersistedPropertyValue } from './Types'
import { type Workflow } from './Workflow'

export const CHILD_STATE_NAMES: Record<ActionType, string> = {
  'reviews.create': 'Reviewed',
  'approvals.create': 'Approved',
  'read_receipts.create': 'Read',
  'reviews.close': '',
}

export class State {
  /**
   * The connected state at the time of initialisation. This will be used to
   * decide whether a state can be reconnected to when adding an automation to
   * reduce the number of changes to the workflow
   */
  readonly initialConnectedState?: State

  workflow: Workflow
  propertyValue: StatePropertyValue
  connectedState?: State
  entryActions: Action[] = []
  exitActions: Action[] = []

  // UI
  focus: boolean = false

  private constructor(workflow: Workflow, propertyValue: StatePropertyValue, config: StateConfig) {
    this.workflow = workflow
    this.propertyValue = propertyValue
    this.entryActions = config.entry?.map((action) => Action.fromConfig(this, action)) || []
    this.exitActions = config.exit?.map((action) => Action.fromConfig(this, action)) || []
    if (propertyValue.connected_to_id) {
      this.connectedState = this.workflow.getState(`${propertyValue.connected_to_id}`)
      this.initialConnectedState = this.workflow.getState(`${propertyValue.connected_to_id}`)
    }
    if (propertyValue.connected_to_uuid) {
      this.connectedState = this.workflow.getState(propertyValue.connected_to_uuid)
    }
    makeAutoObservable(this)
  }

  static fromConfig(
    workflow: Workflow,
    propertyValue: StatePropertyValue,
    config: StateConfig = { entry: [], exit: [] },
  ) {
    return new State(workflow, propertyValue, config)
  }

  toJSON = () => {
    return {
      propertyValue: toJS(this.propertyValue),
      entry: this.entryActions.map((action) => action.toJSON()),
      exit: this.exitActions.map((action) => action.toJSON()),
    }
  }

  get key() {
    if (this.isPersisted) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return this.propertyValue.id!.toString()
    }

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.propertyValue.uuid!
  }

  get isDefault() {
    return this.propertyValue.position === 0
  }

  get isDeletable() {
    return !this.isDefault
  }

  get isChild() {
    return Boolean(this.connectedState)
  }

  get entryAction() {
    if (this.entryActions.length === 0) {
      return undefined
    }
    return this.entryActions[0]
  }

  get exitAction() {
    if (this.exitActions.length === 0) {
      return undefined
    }
    return this.exitActions[0]
  }

  get position() {
    return this.propertyValue.position
  }

  get value() {
    return this.propertyValue.value
  }

  get description() {
    return this.propertyValue.meta.description || ''
  }

  get color() {
    return this.propertyValue.meta.color
  }

  get childStates() {
    return this.workflow.states.filter((state) => state.connectedState === this)
  }

  get isPersisted() {
    return Boolean(this.propertyValue.id)
  }

  setColor = (color: string) => {
    this.propertyValue.meta.color = color
  }

  setName = (name: string) => {
    this.propertyValue.value = name
  }

  setDescription = (description: string) => {
    this.propertyValue.meta.description = description
  }

  setPosition = (position: number) => {
    this.propertyValue.position = position
  }

  setEntryAction = (spec?: ActionSpec) => {
    if (!spec) {
      this.entryActions.forEach((action) => action.destroy())
      this.exitActions.forEach((action) => action.destroy())
      return
    }

    if (this.entryAction && this.entryAction.type === spec.type) {
      // Update config without creating a new state
      this.entryAction.setConfig(spec.config)
      return
    }

    this.entryActions.forEach((action) => action.destroy())
    this.entryActions = [Action.fromConfig(this, spec)]

    if (['reviews.create', 'approvals.create', 'read_receipts.create'].includes(spec.type)) {
      if (this.next?.canBeReconnectedTo(this)) {
        this.next.connectTo(this)
      } else {
        this.createChildState(CHILD_STATE_NAMES[spec.type])
      }
      this.setExitAction({ type: 'reviews.close', config: {} })
    }
  }

  canBeReconnectedTo = (state: State) => {
    return state.next === this && this.entryActions.length === 0 && this.initialConnectedState === state
  }

  setExitAction = (spec?: ActionSpec) => {
    this.exitActions.forEach((action) => action.destroy())

    if (spec) {
      this.exitActions = [Action.fromConfig(this, spec)]
    }
  }

  createChildState = (value?: string) => {
    this.workflow.createState({
      value,
      position: this.position + 1,
      connected_to_id: this.isPersisted ? (this.propertyValue as PersistedPropertyValue).id : null,
      connected_to_uuid: this.isPersisted ? null : (this.propertyValue as UnpersistedPropertyValue).uuid,
    })
  }

  connectTo = (state: State) => {
    this.connectedState = state
    if (state.isPersisted) {
      this.propertyValue.connected_to_id = (state.propertyValue as PersistedPropertyValue).id
    } else {
      this.propertyValue.connected_to_uuid = (state.propertyValue as UnpersistedPropertyValue).uuid
    }
  }

  disconnect = () => {
    if (this.isPersisted) {
      this.propertyValue.connected_to_id = null
      this.connectedState = undefined
      return
    }

    this.destroy()
  }

  destroy = () => {
    if (!this.isDeletable) {
      return
    }
    this.childStates.forEach((childState) => childState.destroy())
    this.workflow.deleteState(this.key)
  }

  insertAt = (position: number) => {
    if (position === 0) {
      throw new Error('Cannot insert state at position 0')
    }

    if (position > this.workflow.states.length) {
      throw new Error(`Cannot insert state at position ${position}`)
    }

    this.workflow.states.forEach((state) => {
      if (state.position >= position) {
        state.setPosition(state.position + 1 + this.childStates.length)
      }
    })
    this.setPosition(position)
    this.childStates.forEach((state, i) => state.setPosition(position + 1 + i))
  }

  get next(): State | null {
    const { states } = this.workflow
    const index = states.findIndex((state) => state === this)
    if (index < 0 || index === states.length - 1) {
      return null
    }
    return states[index + 1]
  }

  setFocus(focus: boolean) {
    if (focus) {
      this.workflow.states.forEach((state) => {
        state.focus = state === this
      })
      return
    }
    this.focus = false
  }
}
